import * as Sentry from "@sentry/react";

import { logError } from "../../utils/error";
import { invokeClaudeStream } from "./apiClient";
import { buildAdaptiveThinkingParams } from "./modelUtils";
import { processStream } from "./streamProcessor";
import { QueryResult, RagQueryParams } from "./types";

export const queryClaudeWithContext = async (
  params: RagQueryParams
): Promise<QueryResult> => {
  try {
    const response = await invokeClaudeStream("claude-query", {
      query: params.query,
      context: params.context,
      messages: params.messages,
      temperature: params.temperature,
      systemPrompt: params.systemPrompt,
      thinkingMode: params.thinkingMode,
      maxTokens: params.maxTokens,
      ...buildAdaptiveThinkingParams({ model: params.model ?? "", effort: params.effort, budgetTokens: params.budgetTokens }),
      model: params.model
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        ok: false,
        error: new Error(errorData.error || "Failed to query Claude")
      };
    }

    const onThinkingDelta = params.thinkingMode
      ? params.onThinkingDelta
      : undefined;

    const result = await processStream({
      stream: response.body!,
      onMessageDelta: params.onMessageDelta,
      onThinkingDelta
    });

    return {
      ok: true,
      response: result.responseText,
      thinking: result.thinking,
      usage: result.usage
    };
  } catch (error) {
    logError("Error querying Claude", error);
    const isAbortError = error instanceof DOMException && error.name === "AbortError";

    // Don't report abort errors to Sentry as they are expected user actions
    if (!isAbortError) {
      Sentry.captureException(error, {
        tags: {
          function: "queryClaudeWithContext",
          module: "claude-query"
        },
        extra: {
          model: params.model,
          thinkingMode: params.thinkingMode,
          hasContext: !!params.context,
          messageCount: params.messages.length
        }
      });
    }

    return {
      ok: false,
      error: isAbortError
        ? new Error("Request was canceled")
        : (error instanceof Error ? error : new Error("An unknown error occurred"))
    };
  }
};
